import { defineStore } from 'pinia'
import { NOTIFICATION_TYPES } from '@/lib/notificationTypes'
import { notificationsApi as api } from '@/api/endpoints/notifications'
import { queryClient } from '@/plugins/vue-query'
import { useWorkspaceStore } from './workspace.store'
import { useAuthStore } from './auth.store'

const MAX_NOTIFICATIONS = 50

/**
 * Normalize a notification from API or Echo payload into a consistent shape.
 * @param {Object} payload - { id?, title, message, type?, link?, created_at?, data? }
 * @returns {Object} Normalized notification
 */
function normalizeNotification(payload) {
  const id = payload.id ?? `n-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const createdAt = payload.created_at ? new Date(payload.created_at) : new Date()
  return {
    id,
    title: payload.title ?? 'Notification',
    message: payload.message ?? payload.body ?? '',
    type: Object.values(NOTIFICATION_TYPES).includes(payload.type) ? payload.type : NOTIFICATION_TYPES.INFO,
    link: payload.link ?? null,
    data: payload.data ?? null,
    read: payload.read ?? false,
    created_at: createdAt.toISOString(),
    created_at_ts: createdAt.getTime(),
  }
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    items: [],
    loading: false,
    lastFetchedAt: null,
  }),

  getters: {
    /** All notifications, newest first */
    notifications: (state) => [...state.items].sort((a, b) => b.created_at_ts - a.created_at_ts),

    unreadCount: (state) => state.items.filter((n) => !n.read).length,

    unread: (state) => state.items.filter((n) => !n.read),

    /** Get notifications for a specific type */
    byType: (state) => (type) => state.items.filter((n) => n.type === type),
  },

  actions: {
    /**
     * Add a notification (e.g. from Echo broadcast).
     * Use this when backend sends real-time events.
     */
    addNotification(payload) {
      const notification = normalizeNotification(payload)
      const existing = this.items.find((n) => n.id === notification.id)
      if (existing) return existing
      this.items = [notification, ...this.items].slice(0, MAX_NOTIFICATIONS)
      return notification
    },

    /**
     * Fetch notifications from API. Backend may expose GET /workspaces/:id/notifications
     * or GET /notifications. Merge with existing to avoid duplicates.
     */
    async fetchNotifications(params = {}) {
      const workspaceId = useWorkspaceStore().getWorkspaceID
      const userId = useAuthStore().user?.id
      if (!workspaceId && !userId) return []

      this.loading = true
      try {
        // Through the query client so the login bootstrap and the dashboard mount share one request.
        const body = await queryClient.fetchQuery({
          queryKey: ['notifications', 'feed', workspaceId, params],
          queryFn: ({ signal }) => api.list(workspaceId, params, { signal }),
          staleTime: 0,
        })
        const list = body?.data?.notifications ?? body?.data ?? []
        const normalized = list.map(normalizeNotification)
        // Merge into the existing list (keyed by id) instead of replacing it —
        // real-time notifications added via addNotification() (Echo pushes)
        // must survive a subsequent fetch even if the backend hasn't indexed
        // them into this response yet.
        const byId = new Map(this.items.map((n) => [n.id, n]))
        normalized.forEach((n) => byId.set(n.id, n))
        this.items = Array.from(byId.values()).slice(0, MAX_NOTIFICATIONS)
        this.lastFetchedAt = Date.now()
        return this.notifications
      } catch (err) {
        if (err.response?.status !== 404) {
          console.warn('[notifications] fetch failed', err)
        }
        return this.notifications
      } finally {
        this.loading = false
      }
    },

    /**
     * Mark a single notification as read (persists to backend).
     */
    async markAsRead(id) {
      const n = this.items.find((item) => item.id === id)
      if (!n) return
      const workspaceId = useWorkspaceStore().getWorkspaceID
      try {
        await api.markRead(workspaceId, id)
        n.read = true
      } catch (err) {
        if (err.response?.status === 404) {
          n.read = true
        } else {
          throw err
        }
      }
    },

    /**
     * Mark all notifications as read (persists to backend).
     */
    async markAllAsRead() {
      const workspaceId = useWorkspaceStore().getWorkspaceID
      try {
        await api.markAllRead(workspaceId)
        this.items.forEach((n) => { n.read = true })
      } catch (err) {
        if (err.response?.status === 404) {
          this.items.forEach((n) => { n.read = true })
        } else {
          throw err
        }
      }
    },

    remove(id) {
      this.items = this.items.filter((n) => n.id !== id)
    },

    clear() {
      this.items = []
    },

    /** Reset when switching workspace or logging out */
    reset() {
      this.items = []
      this.lastFetchedAt = null
    },
  },
})
